/**
 * Utility to sanitize image URLs by removing m= and q= (quality) parameters
 * and ensuring f=webp format is used.
 */

/**
 * Removes m= and q= parameters from a query string and ensures f=webp is set.
 * @param queryString - The query string to sanitize (e.g., 'w=767&h=767&m=8&q=80')
 * @returns Sanitized query string with f=webp (e.g., 'w=767&h=767&f=webp')
 */
export const sanitizeImageQueryString = (queryString: string): string => {
    if (!queryString) {
        return 'f=webp';
    }

    // Split into individual parameters
    const params = queryString.split('&');

    // Filter out m= and q= parameters, and track if f= exists
    let hasFormatParam = false;
    const filteredParams = params
        .map(param => {
            const [key] = param.split('=');

            // Replace f=jpg/jpeg/png with f=webp
            if (key === 'f') {
                hasFormatParam = true;
                return 'f=webp';
            }

            return param;
        })
        .filter((param): param is string => param !== null);

    // Add f=webp if no format parameter exists
    if (!hasFormatParam) {
        filteredParams.push('f=webp');
    }

    return filteredParams.join('&');
};

/**
 * Sanitizes a full image URL by processing its query string.
 * @param url - The full image URL to sanitize
 * @returns Sanitized URL with f=webp format
 */
export const sanitizeImageUrl = (url: string): string => {
    if (!url) {
        return url;
    }

    // Handle URLs with query parameters after the image extension
    const queryIndex = url.indexOf('&');
    if (queryIndex === -1) {
        // No query params, just append f=webp
        return `${url}&f=webp`;
    }

    const baseUrl = url.substring(0, queryIndex);
    const queryString = url.substring(queryIndex + 1);
    const sanitizedQuery = sanitizeImageQueryString(queryString);

    return `${baseUrl}&${sanitizedQuery}`;
};

/**
 * Sanitizes IImageSettings by cleaning up the q property in each viewport
 * @param settings - The image settings to sanitize
 * @returns Sanitized image settings with f=webp format
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const sanitizeImageSettings = <T extends { viewports?: any }>(settings: T | undefined): T | undefined => {
    if (!settings || !settings.viewports) {
        return settings;
    }

    const sanitizedSettings = { ...settings };
    sanitizedSettings.viewports = { ...settings.viewports };

    for (const viewport of Object.keys(sanitizedSettings.viewports)) {
        const viewportSettings = sanitizedSettings.viewports[viewport];
        if (viewportSettings && viewportSettings.q) {
            sanitizedSettings.viewports[viewport] = {
                ...viewportSettings,
                q: sanitizeImageQueryString(viewportSettings.q)
            };
        }
    }

    return sanitizedSettings;
};

/**
 * Mode used by the image resizer when the platform builds its own query string.
 * Kept in sync with @msdyn365-commerce/core-internal source-builder, which defaults to m=6.
 */
const DEFAULT_IMAGE_RESIZER_MODE = 6;

/**
 * Colour sent as the resizer's b= parameter when the image settings do not specify one.
 * The value is never actually painted - see ensureBackgroundParam.
 */
const ALPHA_SWITCH_BACKGROUND = 'FFFFFF';

/**
 * Guarantees the query string carries a b= parameter.
 *
 * The image resizer only returns an alpha-capable webp (VP8X container with an ALPH chunk)
 * when b= is present. Request webp without it and a transparent PNG comes back as plain
 * lossy VP8 with no alpha channel, i.e. flattened onto white. The colour itself is ignored -
 * b=FFFFFF and b=FF0000 produce byte-identical responses - so this behaves purely as a
 * switch, and it adds no alpha chunk (so no extra bytes) for opaque sources.
 * @param queryString - The query string to check
 * @param backgroundColor - Colour configured in site builder, if any
 * @returns The query string with a b= parameter guaranteed
 */
const ensureBackgroundParam = (queryString: string, backgroundColor?: string): string => {
    const hasBackground = queryString.split('&').some(param => param.split('=')[0] === 'b');

    if (hasBackground) {
        return queryString;
    }

    return `${queryString}&b=${backgroundColor ?? ALPHA_SWITCH_BACKGROUND}`;
};

/**
 * Builds the image resizer query string for a single viewport, forcing webp output.
 * The platform only emits w/h/q/m (and hard-codes f=jpg whenever a background colour is set),
 * so the query string is rebuilt here from the same values with f=webp instead.
 * @param viewport - Viewport dimensions from theme settings (w/h, optionally an existing q string)
 * @param settings - The parent image settings, used for quality, mode and background colour
 * @returns Query string such as 'w=665&h=280&m=2&q=80&b=FFFFFF&f=webp'
 */
const buildWebpViewportQuery = (
    viewport: { w?: number; h?: number; q?: string },
    settings: { quality?: number; mode?: number; backgroundColor?: string }
): string => {
    // An explicit query string already describes the resize - only swap the format out.
    if (viewport.q) {
        return ensureBackgroundParam(sanitizeImageQueryString(viewport.q), settings.backgroundColor);
    }

    const params: string[] = [];

    if (viewport.w && viewport.h) {
        params.push(`w=${viewport.w}`, `h=${viewport.h}`);
    }

    params.push(`m=${settings.mode ?? DEFAULT_IMAGE_RESIZER_MODE}`);

    if (settings.quality) {
        params.push(`q=${settings.quality}`);
    }

    params.push(`b=${settings.backgroundColor ?? ALPHA_SWITCH_BACKGROUND}`);
    params.push('f=webp');

    return params.join('&');
};

/**
 * Rewrites image settings so every viewport is requested in webp format.
 * Dimensions, quality and mode are preserved, and a b= parameter is always sent so that
 * transparent sources keep their alpha channel (see ensureBackgroundParam).
 * @param settings - Image settings coming from site builder / theme settings
 * @returns New image settings requesting webp, or the original value when there is nothing to change
 */
export const toWebpImageSettings = <
    T extends {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        viewports?: any;
        quality?: number;
        mode?: number;
        backgroundColor?: string;
    }
>(
    settings: T | undefined
): T | undefined => {
    if (!settings || !settings.viewports) {
        return settings;
    }

    const webpSettings = { ...settings };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const viewports: any = { ...settings.viewports };

    for (const viewport of Object.keys(viewports)) {
        if (viewports[viewport]) {
            viewports[viewport] = {
                ...viewports[viewport],
                q: buildWebpViewportQuery(viewports[viewport], settings)
            };
        }
    }

    webpSettings.viewports = viewports;
    delete webpSettings.backgroundColor;

    return webpSettings;
};

export const getOptimizedImageUrl = (imageUrl: string, width: number | undefined, multiplier: number = 2, height?: number): string => {
    if (!imageUrl || !width) {
        return imageUrl;
    }

    const url = new URL(imageUrl);

    url.searchParams.set('w', (width * multiplier).toString());

    if (height) {
        url.searchParams.set('h', (height * multiplier).toString());
    }

    return url.toString();
};
